import React, { useState, useEffect, useCallback } from "react";
import { useSession } from "hooks/useSession";

/*
  Standalone commission table — deliberately does NOT use the shared
  DataTable / useTableData / tableConfigs components. It fetches, formats,
  and renders com_data entirely on its own, so it can't break from
  assumptions baked into those shared components.

  API response shape handled:
  {
    "com_data": [ { name, start_slab, end_slab, type, commission }, ... ],
    "data": [ ... ],   // ignored here on purpose
    "status": true
  }
*/

const API_URL = "https://pro.mokshiq.in/retailer_commission";

const formatSlab = (row) => {
  const start = row.start_slab ?? 0;
  const end = row.end_slab ?? 0;
  if (start === 0 && end === 0) return "—";
  return `₹${start} – ₹${end}`;
};

const formatCommission = (row) => {
  const val = row.commission ?? "0";
  return row.type === "percent" ? `${val}%` : `₹${val}`;
};

const UserOtherCommissionManager = () => {
  const { userid } = useSession();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [refresh, setRefresh] = useState(0);

  const fetchComData = useCallback(async () => {
    if (!userid) return;
    setLoading(true);
    setError(null);
    const headers =  {} ;
    const token = localStorage.getItem("authToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    try {
      const res = await fetch(`${API_URL}?id=${encodeURIComponent(userid)}`, {headers});
      const json = await res.json();
      if (json?.status) {
        setRows(Array.isArray(json.com_data) ? json.com_data : []);
      } else {
        setError(json?.message || "Failed to load commission data");
        setRows([]);
      }
    } catch (err) {
      console.error("retailer_commission fetch failed", err);
      setError("Network error while loading commission data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userid]);

  useEffect(() => {
    fetchComData();
  }, [fetchComData, refresh]);

  const filteredRows = rows.filter((r) =>
    (r.name || "").toLowerCase().includes(search.toLowerCase())
  );

  const handleExportCsv = () => {
    if (filteredRows.length === 0) return;
    const header = ["Name", "Start Slab", "End Slab", "Type", "Commission"];
    const lines = filteredRows.map((r) =>
      [r.name, r.start_slab, r.end_slab, r.type, r.commission]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "commission_data.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white d-flex flex-wrap align-items-center justify-content-between gap-2 py-3">
        <h5 className="mb-0 fw-bold">Commission Slabs</h5>
        <div className="d-flex align-items-center gap-2">
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: 220 }}
            placeholder="Search by service name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setRefresh((p) => p + 1)}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={handleExportCsv}
            disabled={filteredRows.length === 0}
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="card-body p-0">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3 text-muted mb-0">Loading commission data...</p>
          </div>
        ) : error ? (
          <div className="text-center py-5">
            <p className="text-danger mb-2">{error}</p>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => setRefresh((p) => p + 1)}
            >
              Try Again
            </button>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="text-center py-5 text-muted">
            No commission records found{search ? ` for "${search}"` : ""}.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>Service Name</th>
                  <th>Slab Range</th>
                  <th>Type</th>
                  <th className="text-end">Commission</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, idx) => (
                  <tr key={`${row.name}-${row.start_slab}-${row.end_slab}-${idx}`}>
                    <td className="text-muted">{idx + 1}</td>
                    <td className="fw-medium">{row.name}</td>
                    <td>{formatSlab(row)}</td>
                    <td>
                      <span
                        className={`badge ${
                          row.type === "percent" ? "bg-info-subtle text-info" : "bg-secondary-subtle text-secondary"
                        }`}
                      >
                        {row.type}
                      </span>
                    </td>
                    <td className="text-end fw-semibold">{formatCommission(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !error && (
        <div className="card-footer bg-white text-muted small py-2">
          Showing {filteredRows.length} of {rows.length} record{rows.length === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
};

export default UserOtherCommissionManager;